import { ExternalLink, GitBranch } from "lucide-react";
import { toast } from "sonner";

import { Input } from "@monorepo-apps/ui/src/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import { isHttpUrl } from "@/application/use-cases/b2c/is-http-url";
import { useB2cTaskActivity } from "@/application/use-cases/b2c/use-b2c-board.queries";
import { StatusSelect } from "@/presentation/components/board/status-select";
import { B2cActivityFeed } from "@/presentation/components/b2c/b2c-activity-feed";
import { CopyCode } from "@/presentation/components/b2c/copy-code";
import { Field } from "@/presentation/components/delivery-tasks/field";
import { StatusDot } from "@/presentation/components/status/status-dot";
import {
  ITERATION_STATUS_LABEL,
  ITERATION_STATUS_TONE,
  KIND_LABEL,
  KIND_ORDER,
  PRIORITY_LABEL,
  PRIORITY_ORDER,
} from "@/presentation/data/b2c-board";
import type { B2cTaskPatch } from "@/infrastructure/repositories/b2c-board.supabase.repository";
import type {
  B2cIterationRow,
  B2cStageRow,
  B2cTaskKind,
  B2cTaskPriority,
  B2cTaskRow,
  B2cWorkstreamRow,
} from "@/infrastructure/supabase/b2c-board.types";

/**
 * Full detail for one B2C task. Text fields save on blur, selects on change —
 * the same convention as the delivery-tasks drawer.
 */
export function B2cTaskDrawerContent({
  task,
  workstream,
  iterations,
  stagesByKey,
  canWrite,
  onPatch,
}: {
  task: B2cTaskRow;
  workstream: B2cWorkstreamRow | null;
  /** The workstream's iterations, ascending. */
  iterations: B2cIterationRow[];
  stagesByKey: Map<string, B2cStageRow>;
  canWrite: boolean;
  onPatch: (patch: B2cTaskPatch) => void;
}) {
  const activityQ = useB2cTaskActivity(task.id);
  const textField = (
    field: "description" | "acceptance_md" | "notes",
    label: string,
    rows: number,
    placeholder: string
  ) => (
    <Field label={label}>
      <Textarea
        key={`${task.id}-${field}-${task[field] ?? ""}`}
        defaultValue={task[field] ?? ""}
        rows={rows}
        disabled={!canWrite}
        placeholder={placeholder}
        aria-label={label}
        className="text-[13px]"
        onBlur={(e) => {
          const next = e.target.value.trim() || null;
          if (next !== task[field]) onPatch({ [field]: next } as B2cTaskPatch);
        }}
      />
    </Field>
  );

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <header className="border-border/60 flex flex-col gap-2 border-b px-6 py-5">
        <div className="flex flex-wrap items-center gap-2">
          <CopyCode code={task.code} className="text-[12px]" />
          {task.is_e2e_proof ? (
            <span className="text-primary font-data text-[10px] font-bold tracking-[0.12em] uppercase">
              End-to-end proof
            </span>
          ) : null}
          {workstream ? (
            <span className="font-data text-muted-foreground text-[10px] tracking-[0.14em] uppercase">
              {workstream.code} · {workstream.title}
            </span>
          ) : null}
        </div>
        <Textarea
          key={`${task.id}-title-${task.title}`}
          defaultValue={task.title}
          rows={2}
          maxLength={300}
          disabled={!canWrite}
          aria-label="Task title"
          className="resize-none border-transparent bg-transparent px-0 text-base font-medium shadow-none focus-visible:border-transparent"
          onBlur={(e) => {
            const next = e.target.value.trim();
            if (next && next !== task.title) onPatch({ title: next });
          }}
        />
      </header>

      <div className="flex flex-col gap-5 px-6 py-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Status">
            <StatusSelect
              value={task.status}
              onChange={(status) => onPatch({ status })}
              disabled={!canWrite}
            />
          </Field>
          <Field label="Iteration">
            <Select
              value={task.iteration_id ?? ""}
              onValueChange={(v) => onPatch({ iteration_id: v })}
              disabled={!canWrite}
            >
              <SelectTrigger aria-label="Iteration">
                <SelectValue placeholder="Not in an iteration" />
              </SelectTrigger>
              <SelectContent>
                {iterations.map((i) => (
                  <SelectItem
                    key={i.id}
                    value={i.id}
                    disabled={i.status === "merged" && i.id !== task.iteration_id}
                  >
                    <span className="inline-flex items-center gap-2">
                      <StatusDot tone={ITERATION_STATUS_TONE[i.status]} />
                      Iteration {i.number} · {ITERATION_STATUS_LABEL[i.status]}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Priority">
            <Select
              value={task.priority}
              onValueChange={(v) => onPatch({ priority: v as B2cTaskPriority })}
              disabled={!canWrite}
            >
              <SelectTrigger aria-label="Priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_ORDER.map((p) => (
                  <SelectItem key={p} value={p}>
                    {PRIORITY_LABEL[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Kind">
            <Select
              value={task.kind}
              onValueChange={(v) => onPatch({ kind: v as B2cTaskKind })}
              // E2E is set by the proof flag, not chosen here (migration 0143 keeps them equal).
              disabled={!canWrite || task.is_e2e_proof}
            >
              <SelectTrigger aria-label="Kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(task.is_e2e_proof ? KIND_ORDER : KIND_ORDER.filter((k) => k !== "e2e")).map(
                  (k) => (
                    <SelectItem key={k} value={k}>
                      {KIND_LABEL[k]}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </Field>
        </div>

        {textField("description", "Description", 5, "What this task delivers")}
        {textField("acceptance_md", "Acceptance", 4, "- [ ] How we know it's done")}
        {textField("notes", "Notes", 3, "Anything worth remembering")}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Branch">
            <Input
              key={`${task.id}-branch-${task.branch_name ?? ""}`}
              defaultValue={task.branch_name ?? ""}
              disabled={!canWrite}
              placeholder="feat/…"
              aria-label="Branch"
              className="font-data text-[12px]"
              onBlur={(e) => {
                const next = e.target.value.trim() || null;
                if (next !== task.branch_name) onPatch({ branch_name: next });
              }}
            />
          </Field>
          <Field label="Pull request">
            <Input
              key={`${task.id}-pr-${task.pr_url ?? ""}`}
              defaultValue={task.pr_url ?? ""}
              disabled={!canWrite}
              placeholder="https://github.com/…/pull/…"
              aria-label="Pull request link"
              className="font-data text-[12px]"
              onBlur={(e) => {
                const next = e.target.value.trim() || null;
                if (next && !isHttpUrl(next)) {
                  toast.error("The PR link must start with https://");
                  e.target.value = task.pr_url ?? "";
                  return;
                }
                if (next !== task.pr_url) onPatch({ pr_url: next });
              }}
            />
          </Field>
        </div>

        {task.branch_name || task.pr_url ? (
          <div className="flex flex-wrap gap-3 text-[12px]">
            {task.branch_name ? (
              <span className="font-data text-muted-foreground flex items-center gap-1">
                <GitBranch className="size-3.5" aria-hidden />
                {task.branch_name}
              </span>
            ) : null}
            {task.pr_url ? (
              <a
                href={task.pr_url}
                target="_blank"
                rel="noreferrer"
                className="text-primary flex items-center gap-1 font-semibold hover:underline"
              >
                <ExternalLink className="size-3.5" aria-hidden />
                Open the PR
              </a>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <span className="eyebrow">Activity</span>
          <B2cActivityFeed
            entries={(activityQ.data?.pages ?? []).flatMap((p) => p.rows)}
            isLoading={activityQ.isLoading}
            isError={activityQ.isError}
            stagesByKey={stagesByKey}
            hasMore={activityQ.hasNextPage}
            isLoadingMore={activityQ.isFetchingNextPage}
            onLoadMore={() => void activityQ.fetchNextPage()}
          />
        </div>
      </div>
    </div>
  );
}
